import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { get, post, patch, toastError } from "@/lib/api";
import { fmtDate, num, todayISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EmptyState } from "@/components/common/States";
import { Users, Plus, Pencil, CalendarPlus, Loader2, ArrowRight, Repeat } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { dayLabel, weekOf } from "./AccommodationBits";

const DAY_PX = 14;
const dayIndex = (d, start) => Math.round((new Date(d) - new Date(start)) / 86400000);

/* ------------------------------------------------------------------ crew form ---- */

const F = ({ label, children, hint, className }) => (
  <div className={cn("space-y-1.5", className)}>
    <Label className="text-[12px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[11.5px] leading-4 text-muted-foreground">{hint}</p>}
  </div>
);

export const CrewSheet = ({ open, onOpenChange, projectId, crew }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({});
  useEffect(() => {
    if (!open) return;
    setF(crew ? { name: crew.name, employer_name: crew.employer_name || "", size: crew.size || 0, on_days: crew.rotation?.on_days || 14, off_days: crew.rotation?.off_days ?? 14, anchor_date: crew.rotation?.anchor_date || todayISO(), start_date: crew.start_date || "", end_date: crew.end_date || "", notes: crew.notes || "" }
      : { name: "", employer_name: "", size: 8, on_days: 14, off_days: 14, anchor_date: todayISO(), start_date: "", end_date: "", notes: "" });
  }, [open, crew?.id]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const m = useMutation({
    mutationFn: () => {
      const body = { name: f.name, employer_name: f.employer_name || null, size: Number(f.size) || 0, rotation: { on_days: Number(f.on_days), off_days: Number(f.off_days), anchor_date: f.anchor_date }, start_date: f.start_date || null, end_date: f.end_date || null, notes: f.notes || null };
      return crew ? patch(`/innkvartering/crews/${crew.id}`, body) : post("/innkvartering/crews", { ...body, project_id: projectId });
    },
    onSuccess: () => { toast.success(crew ? "Crew oppdatert" : "Crew opprettet"); qc.invalidateQueries({ queryKey: ["ink"] }); onOpenChange(false); },
    onError: (e) => toastError(e, "Kunne ikke lagre crew"),
  });
  const valid = f.name?.trim() && Number(f.on_days) > 0 && f.anchor_date;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex w-[440px] max-w-[94vw] flex-col gap-0 overflow-y-auto p-0" data-testid="crew-sheet">
        <SheetHeader className="border-b px-6 py-5 text-left">
          <SheetTitle className="text-[16px]">{crew ? `Rediger ${crew.name}` : "Nytt crew"}</SheetTitle>
          <SheetDescription className="text-[12.5px]">Crewet er behovet: størrelse × rotasjon gir forventet belegg. Personer knyttes til crewet etterpå.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-4 px-6 py-5">
          <F label="Navn"><Input value={f.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Crew Charlie" data-testid="crew-name-input" /></F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Arbeidsgiver"><Input value={f.employer_name || ""} onChange={(e) => set("employer_name", e.target.value)} data-testid="crew-employer-input" /></F>
            <F label="Personer"><Input type="number" min={0} value={f.size ?? ""} onChange={(e) => set("size", e.target.value)} data-testid="crew-size-input" /></F>
          </div>
          <div className="rounded-lg border p-3">
            <div className="mb-2 flex items-center gap-1.5 text-[12.5px] font-medium"><Repeat className="h-3.5 w-3.5" /> Rotasjon</div>
            <div className="grid grid-cols-3 gap-3">
              <F label="Dager på"><Input type="number" min={1} value={f.on_days ?? ""} onChange={(e) => set("on_days", e.target.value)} data-testid="crew-on-days-input" /></F>
              <F label="Dager av"><Input type="number" min={0} value={f.off_days ?? ""} onChange={(e) => set("off_days", e.target.value)} data-testid="crew-off-days-input" /></F>
              <F label="Første dag på"><Input type="date" value={f.anchor_date || ""} onChange={(e) => set("anchor_date", e.target.value)} data-testid="crew-anchor-input" /></F>
            </div>
            <p className="mt-2 text-[11.5px] text-muted-foreground">{Number(f.on_days) > 0 ? `${f.on_days}/${f.off_days || 0} – ${f.size || 0} personer på camp i ${f.on_days} dager, så ${f.off_days || 0} dager fri.` : "Sett dager på for å definere rotasjonen."}</p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Fra (valgfritt)"><Input type="date" value={f.start_date || ""} onChange={(e) => set("start_date", e.target.value)} data-testid="crew-start-input" /></F>
            <F label="Til (valgfritt)"><Input type="date" value={f.end_date || ""} onChange={(e) => set("end_date", e.target.value)} data-testid="crew-end-input" /></F>
          </div>
          <F label="Notat"><Input value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="crew-notes-input" /></F>
        </div>
        <div className="mt-auto flex justify-end gap-2 border-t px-6 py-4">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="crew-cancel-button">Avbryt</Button>
          <Button onClick={() => m.mutate()} disabled={!valid || m.isPending} data-testid="crew-submit-button">{m.isPending && <Loader2 className="animate-spin" />} {crew ? "Lagre" : "Opprett crew"}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};

/* ------------------------------------------------------------------ plan next rotation (preview → confirm) ---- */

export const PlanRotationDialog = ({ crew, open, onOpenChange }) => {
  const qc = useQueryClient();
  const preview = useQuery({ queryKey: ["ink", "rotation-preview", crew?.id], queryFn: () => post(`/innkvartering/crews/${crew.id}/plan-next-rotation/preview`, {}), enabled: open && Boolean(crew?.id), staleTime: 0 });
  const confirm = useMutation({
    mutationFn: () => post(`/innkvartering/crews/${crew.id}/plan-next-rotation/confirm`, { stays: preview.data.stays }),
    onSuccess: (d) => { toast.success(`${d.created.length} opphold planlagt for ${crew.name}` + (d.skipped.length ? ` · ${d.skipped.length} hoppet over` : "")); qc.invalidateQueries({ queryKey: ["ink"] }); onOpenChange(false); },
    onError: (e) => toastError(e, "Kunne ikke planlegge rotasjonen"),
  });
  const p = preview.data;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[560px]" data-testid="plan-rotation-dialog">
        <DialogHeader>
          <DialogTitle>Planlegg neste rotasjon – {crew?.name}</DialogTitle>
          <DialogDescription>Forhåndsvisning. Hver person får sengen fra forrige rotasjon når den er ledig, ellers første ledige seng. Ingenting skrives før du bekrefter.</DialogDescription>
        </DialogHeader>
        {preview.isLoading ? <Skeleton className="h-40" /> : preview.isError ? <p className="text-[13px] text-rose-600">Kunne ikke hente forhåndsvisning.</p> : p && (
          <div className="space-y-3">
            <p className="text-[13.5px]" data-testid="plan-rotation-message">{p.block ? <><span className="font-medium">{dayLabel(p.block.start)} – {dayLabel(p.block.end)}</span> · {p.message}</> : p.message}</p>
            {p.stays.length > 0 && (
              <ul className="max-h-64 divide-y overflow-y-auto rounded-lg border text-[13px]" data-testid="plan-rotation-list">
                {p.stays.map((s) => (
                  <li key={s.person_id} className="flex items-center gap-3 px-3 py-2" data-testid={`plan-rotation-row-${s.person_id}`}>
                    <span className="min-w-0 flex-1 truncate">{s.person_name} <span className="text-muted-foreground">· {s.employer_name}</span></span>
                    <span className="font-semibold tabular">{s.operational_number}</span>
                    <span className={cn("w-16 text-right text-[11px]", s.reused_bed ? "text-muted-foreground" : "text-amber-700 dark:text-amber-300")}>{s.reused_bed ? "samme seng" : "ny seng"}</span>
                  </li>
                ))}
              </ul>
            )}
            {p.unplaced.length > 0 && <p className="text-[12.5px] text-rose-600" data-testid="plan-rotation-unplaced">{p.unplaced.length} uten ledig seng: {p.unplaced.map((u) => u.name).join(", ")}</p>}
          </div>
        )}
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="plan-rotation-cancel-button">Avbryt</Button>
          <Button onClick={() => confirm.mutate()} disabled={!p?.stays?.length || confirm.isPending} data-testid="plan-rotation-confirm-button">{confirm.isPending && <Loader2 className="animate-spin" />} Bekreft {p?.stays?.length ? `${p.stays.length} opphold` : ""}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ------------------------------------------------------------------ rotation lanes ---- */

/**
 * Crew rotation timeline (design_guidelines → crew_rotation_timeline): one lane per crew, on-duty blocks, handover ticks,
 * a today line and an expected-occupancy strip against available beds. Pure CSS positioning – no chart lib.
 */
export const CrewRotation = ({ projectId, data, isLoading, canWrite, hasPersons, selectedCrewId, onSelectCrew }) => {
  const [sheet, setSheet] = useState(null); // {crew} | {} for new
  const [planFor, setPlanFor] = useState(null);
  const range = data?.range;
  const days = range ? dayIndex(range.end, range.start) : 0;
  const width = days * DAY_PX;
  const today = todayISO();
  const weeks = useMemo(() => {
    if (!range) return [];
    const out = [];
    for (let i = 0; i <= days; i++) {
      const d = new Date(range.start); d.setDate(d.getDate() + i);
      if (d.getDay() === 1) out.push({ x: i * DAY_PX, label: weekOf(d.toISOString().slice(0, 10)), date: d.toISOString().slice(0, 10) });
    }
    return out;
  }, [range, days]);
  const maxExpected = Math.max(data?.beds_available || 0, ...(data?.series || []).map((s) => s.expected), 1);

  if (isLoading || !data) return <Skeleton className="h-72" data-testid="crew-rotation-loading" />;
  if (!data.crews.length) {
    return (
      <div className="space-y-4">
        <EmptyState icon={Users} title="Ingen crew registrert" description="Crew med rotasjon gir forventet belegg og driver planleggingen av opphold." action={canWrite && <Button onClick={() => setSheet({})} data-testid="crew-new-button"><Plus /> Nytt crew</Button>} testId="crew-rotation-empty" className="py-12" />
        <CrewSheet open={Boolean(sheet)} onOpenChange={(o) => !o && setSheet(null)} projectId={projectId} crew={sheet?.crew} />
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="crew-rotation">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-[12.5px] text-muted-foreground">{data.crews.length} crew · {num(data.crews.reduce((a, c) => a + (c.size || 0), 0))} personer i rotasjon · {num(data.beds_available)} senger tilgjengelig</p>
        {canWrite && <Button size="sm" variant="outline" className="ml-auto h-8" onClick={() => setSheet({})} data-testid="crew-new-button"><Plus /> Nytt crew</Button>}
      </div>

      <TooltipProvider delayDuration={100}>
        <div className="overflow-x-auto rounded-lg border" data-testid="crew-rotation-timeline">
          <div className="grid" style={{ gridTemplateColumns: `200px ${width}px` }}>
            {/* header */}
            <div className="sticky left-0 z-[2] border-b border-r bg-background px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Crew</div>
            <div className="relative h-8 border-b">
              {weeks.map((w) => <div key={w.date} className="absolute top-0 flex h-full items-center border-l border-border/60 pl-1.5 text-[11px] text-muted-foreground" style={{ left: w.x }}><span className="font-mono">{w.label}</span><span className="ml-1 hidden sm:inline">{fmtDate(w.date, "d.M")}</span></div>)}
              <div className="absolute top-0 h-full w-px bg-primary" style={{ left: dayIndex(today, range.start) * DAY_PX }} aria-hidden />
            </div>
            {/* lanes */}
            {data.crews.map((c) => {
              const selected = c.id === selectedCrewId;
              return (
                <div key={c.id} className="contents" data-testid={`crew-rotation-row-${c.id}`}>
                  <button type="button" onClick={() => onSelectCrew?.(selected ? null : c.id)} className={cn("sticky left-0 z-[2] flex min-h-[52px] flex-col justify-center border-b border-r bg-background px-3 py-1.5 text-left transition-colors duration-150 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring", selected && "bg-primary/[0.06]")} data-testid={`crew-rotation-name-${c.id}`}>
                    <span className="flex items-center gap-2 text-[13px] font-medium leading-5"><span className={cn("h-1.5 w-1.5 rounded-full", c.state.is_on ? "bg-emerald-500" : "bg-border")} aria-hidden />{c.name}</span>
                    <span className="truncate text-[11.5px] leading-4 text-muted-foreground">{c.employer_name} · {c.size} pers · {c.state.label}</span>
                  </button>
                  <div className={cn("relative min-h-[52px] border-b", selected && "bg-primary/[0.04]")}>
                    {weeks.map((w) => <div key={w.date} className="absolute top-0 h-full w-px bg-border/40" style={{ left: w.x }} aria-hidden />)}
                    <div className="absolute top-0 h-full w-px bg-primary/70" style={{ left: dayIndex(today, range.start) * DAY_PX }} aria-hidden />
                    {c.blocks.filter((b) => b.kind === "on").map((b, i) => {
                      const x0 = Math.max(0, dayIndex(b.start, range.start)) * DAY_PX;
                      const x1 = Math.min(days, dayIndex(b.end, range.start)) * DAY_PX;
                      if (x1 <= x0) return null;
                      return (
                        <Tooltip key={i}>
                          <TooltipTrigger asChild>
                            <div className={cn("absolute top-3 h-7 rounded-md border text-[11px] leading-7", selected ? "border-primary/60 bg-primary/15" : "border-foreground/40 bg-foreground/10", "px-2 truncate")} style={{ left: x0, width: x1 - x0 }} data-testid={`crew-rotation-block-${c.id}-${b.start}`}>
                              {x1 - x0 > 60 && <span className="font-mono tabular">{c.size}</span>}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-[12px]">{c.name} på skift {dayLabel(b.start)} – {dayLabel(b.end)} · {c.size} personer</TooltipContent>
                        </Tooltip>
                      );
                    })}
                    {c.blocks.filter((b) => b.kind === "on").map((b) => (
                      <span key={`tick-${b.start}`} className="absolute top-1 h-1.5 w-px bg-foreground/60" style={{ left: dayIndex(b.start, range.start) * DAY_PX }} aria-hidden />
                    ))}
                  </div>
                </div>
              );
            })}
            {/* expected occupancy strip */}
            <div className="sticky left-0 z-[2] flex min-h-[72px] flex-col justify-center border-r bg-background px-3 py-1.5">
              <span className="text-[12.5px] font-medium leading-5">Forventet belegg</span>
              <span className="text-[11.5px] leading-4 text-muted-foreground">vs {num(data.beds_available)} senger</span>
            </div>
            <div className="relative min-h-[72px]" data-testid="crew-rotation-expected">
              <div className="absolute inset-x-0 border-t border-dashed border-foreground/40" style={{ bottom: `${(data.beds_available / maxExpected) * 100}%` }} aria-hidden />
              {data.series.map((s, i) => {
                const h = (s.expected / maxExpected) * 100;
                const over = s.expected > data.beds_available;
                return (
                  <Tooltip key={s.date}>
                    <TooltipTrigger asChild>
                      <div className={cn("absolute bottom-0 rounded-t-sm", over ? "bg-rose-500" : s.date < today ? "bg-foreground/20" : "bg-foreground/45")} style={{ left: i * DAY_PX + 2, width: DAY_PX - 4, height: `${h}%` }} data-testid={over ? `crew-rotation-shortfall-${s.date}` : undefined} />
                    </TooltipTrigger>
                    <TooltipContent side="top" className="text-[12px]">{dayLabel(s.date)}: {s.expected} personer{over ? ` – ${s.expected - data.beds_available} flere enn senger` : ""}{s.crews?.length ? ` · ${s.crews.join(", ")}` : ""}</TooltipContent>
                  </Tooltip>
                );
              })}
              <div className="absolute top-0 h-full w-px bg-primary/70" style={{ left: dayIndex(today, range.start) * DAY_PX }} aria-hidden />
            </div>
          </div>
        </div>
      </TooltipProvider>

      {/* crew cards: state, next change, actions */}
      <ul className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="crew-cards">
        {data.crews.map((c) => (
          <li key={c.id} className={cn("rounded-lg border p-3.5", c.id === selectedCrewId && "border-primary/60")} data-testid={`crew-card-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="text-[13.5px] font-semibold leading-5">{c.name}</div>
                <div className="truncate text-[12px] text-muted-foreground">{c.employer_name} · {c.size} personer{hasPersons ? ` · ${c.members} registrert` : ""}</div>
              </div>
              <span className={cn("shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-medium", c.state.is_on ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300" : "bg-muted text-muted-foreground")}>{c.state.is_on ? "På skift" : "Fri"}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-[12px]">
              <div><div className="text-muted-foreground">Rotasjon</div><div className="font-mono font-medium tabular">{c.state.label}</div></div>
              <div><div className="text-muted-foreground">{c.state.next_kind === "on" ? "Kommer" : "Reiser"}</div><div className="font-medium">{c.state.next_change ? `${dayLabel(c.state.next_change)} · ${c.state.days_to_change} d` : "–"}</div></div>
            </div>
            {canWrite && (
              <div className="mt-3 flex flex-wrap gap-2">
                {hasPersons && c.next_block && <Button size="sm" variant="outline" className="h-7 px-2 text-[12px]" onClick={() => setPlanFor(c)} data-testid={`crew-plan-rotation-button-${c.id}`}><CalendarPlus className="h-3.5 w-3.5" /> Planlegg neste rotasjon</Button>}
                <Button size="sm" variant="ghost" className="h-7 px-2 text-[12px]" onClick={() => setSheet({ crew: c })} data-testid={`crew-edit-button-${c.id}`}><Pencil className="h-3.5 w-3.5" /> Rediger</Button>
              </div>
            )}
          </li>
        ))}
      </ul>

      {data.handovers?.length > 0 && (
        <div data-testid="crew-handovers">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Neste bytter</h3>
          <ul className="mt-2 divide-y rounded-lg border text-[13px]">
            {data.handovers.filter((h) => h.date > today).slice(0, 6).map((h) => (
              <li key={h.date} className="flex items-center gap-3 px-3 py-2" data-testid={`crew-handover-${h.date}`}>
                <span className="w-24 shrink-0 font-medium">{dayLabel(h.date)} <span className="font-mono text-[11px] font-normal text-muted-foreground">{weekOf(h.date)}</span></span>
                <span className="min-w-0 flex-1 truncate text-foreground/85">{h.label}</span>
                <span className={cn("shrink-0 font-mono text-[12px] tabular", h.net > 0 ? "text-amber-700 dark:text-amber-300" : "text-muted-foreground")}><ArrowRight className="inline h-3 w-3" /> {h.net > 0 ? "+" : ""}{h.net}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <CrewSheet open={Boolean(sheet)} onOpenChange={(o) => !o && setSheet(null)} projectId={projectId} crew={sheet?.crew} />
      <PlanRotationDialog crew={planFor} open={Boolean(planFor)} onOpenChange={(o) => !o && setPlanFor(null)} />
    </div>
  );
};

export const useCrewData = (projectId, enabled = true) => useQuery({ queryKey: ["ink", "crews", projectId], queryFn: () => get(`/innkvartering/projects/${projectId}/crews`), enabled: Boolean(projectId) && enabled });
